/**
 * Marketing Effectiveness Closed-Loop Analysis Page
 * 营销效果闭环分析页面 — 对标Marvy2.0数据分析Agent
 *
 * Features:
 * - 顶部统计卡片（ROAS总收入、总支出、ROAS倍数、转化率）
 * - 时间范围选择器（7天/30天/90天）
 * - ROAS趋势折线图（双Y轴：支出vs收入）
 * - 渠道对比柱状图（各渠道ROAS对比）
 * - 转化漏斗图（曝光→点击→加购→购买→复购）
 * - 归因饼图（各渠道收入贡献占比）
 * - 渠道明细Table
 * - 底部优化建议Card（基于数据的Agent建议）
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Card, Row, Col, Table, Segmented, Spin, Tag, Badge, Alert, message,
} from 'antd';
import {
  DollarOutlined, RiseOutlined, FallOutlined, EyeOutlined,
  AimOutlined, TeamOutlined, BulbOutlined, TrophyOutlined,
  WarningOutlined, CheckCircleOutlined, ThunderboltOutlined,
} from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import StatCard from '../../components/StatCard';
import {
  getOverview, getChannelData, getFunnelData,
  getAttribution, getTrend,
} from '../../api/marketing';

/* ============================================================
   Theme Constants (与项目全局保持一致)
   ============================================================ */
const TEXT_SECONDARY = 'var(--text-secondary, #A0AEC0)';
const TEXT_PRIMARY = 'var(--text-primary, #fff)';
const TEXT_TERTIARY = 'var(--text-tertiary, #4A5568)';
const BORDER_SUBTLE = 'var(--border-subtle, rgba(255,255,255,0.04))';
const BRAND_PRIMARY = 'var(--accent-primary, #F5C840)';
const CHART_COLORS = {
  TikTok: '#00D4FF',
  '亚马逊': '#FF9900',
  Facebook: '#1877F2',
  Google: '#4285F4',
  Instagram: '#F472B6',
};

/* ============================================================
   Marketing Page Component
   ============================================================ */
const MarketingPage = () => {
  const [period, setPeriod] = useState(30);
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [channelData, setChannelData] = useState([]);
  const [funnelData, setFunnelData] = useState(null);
  const [attribution, setAttribution] = useState([]);
  const [trend, setTrend] = useState(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    return () => { mountedRef.current = false; };
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, cd, fd, at, tr] = await Promise.all([
        getOverview(period),
        getChannelData(period),
        getFunnelData(),
        getAttribution(),
        getTrend(period),
      ]);
      if (!mountedRef.current) return;
      setOverview(ov);
      setChannelData(cd);
      setFunnelData(fd);
      setAttribution(at);
      setTrend(tr);
    } catch {
      message.error('数据加载失败，请重试');
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  /* ============================================================
     Helper: 生成优化建议
     ============================================================ */
  const generateSuggestions = useCallback(() => {
    if (!channelData.length || !overview) return [];

    const suggestions = [];
    const bestChannel = channelData.reduce((a, b) => (a.roas > b.roas ? a : b));
    const worstChannel = channelData.reduce((a, b) => (a.roas < b.roas ? a : b));

    // 建议1：增加表现最佳渠道的预算
    if (bestChannel.roas >= 3.5) {
      suggestions.push({
        type: 'success',
        icon: <TrophyOutlined />,
        title: `增加${bestChannel.channel}预算分配`,
        desc: `${bestChannel.channel} ROAS达${bestChannel.roas.toFixed(1)}x，远高于平均水平${overview.roasRatio}x。建议将该渠道日预算提升30-50%，预计可带来约$${Math.round(bestChannel.revenue * 0.3).toLocaleString()}的增量收入。`,
      });
    }

    // 建议2：优化表现不佳渠道
    if (worstChannel.roas < 2.5) {
      suggestions.push({
        type: 'warning',
        icon: <WarningOutlined />,
        title: `优化${worstChannel.channel}投放策略`,
        desc: `${worstChannel.channel} ROAS仅${worstChannel.roas.toFixed(1)}x，低于整体水平。建议：审查受众定向精准度、A/B测试创意素材、或重新分配预算至表现更优渠道。`,
      });
    }

    // 建议3：针对CTR优化
    const lowCtrChannels = channelData.filter(c => c.ctr < 3.5);
    if (lowCtrChannels.length > 0) {
      suggestions.push({
        type: 'info',
        icon: <BulbOutlined />,
        title: '提升低CTR渠道的创意质量',
        desc: `${lowCtrChannels.map(c => c.channel).join('、')}的CTR低于行业基准。建议制作更多原生风格视频素材、添加CTA文案、并在前3秒抓住用户注意力。`,
      });
    }

    // 建议4：漏斗优化
    if (funnelData) {
      const cartToPurchaseRate = funnelData.rates[3];
      if (cartToPurchaseRate < 50) {
        suggestions.push({
          type: 'info',
          icon: <AimOutlined />,
          title: '提升加购到购买的转化率',
          desc: `当前加购→购买转化率为${cartToPurchaseRate}%，存在较大优化空间。建议：优化结账流程、提供限时折扣、添加信任标识（Trust Badge）、设置弃购召回邮件。`,
        });
      }
    }

    // 建议5：多渠道归因优化
    suggestions.push({
      type: 'success',
      icon: <CheckCircleOutlined />,
      title: '实施多触点归因模型',
      desc: '当前数据显示用户平均经过2.8个触点才完成转化。建议从最后点击归因切换到数据驱动归因模型，更准确评估各渠道真实贡献，优化整体预算分配效率。',
    });

    return suggestions;
  }, [channelData, overview, funnelData]);

  /* ============================================================
     Chart Options
     ============================================================ */

  // ROAS趋势折线图 — 双Y轴：支出 vs 收入
  const trendOption = trend ? {
    tooltip: {
      trigger: 'axis',
      backgroundColor: 'rgba(17,24,39,0.9)',
      borderColor: BORDER_SUBTLE,
      textStyle: { color: '#fff' },
      formatter: (params) => {
        let str = `<div style="font-weight:600;margin-bottom:4px">${params[0].axisValue}</div>`;
        params.forEach(p => {
          str += `<div style="display:flex;align-items:center;gap:6px;margin:2px 0">
            <span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${p.color}"></span>
            <span>${p.seriesName}: $${p.value.toLocaleString()}</span>
          </div>`;
        });
        return str;
      },
    },
    legend: {
      data: ['支出', '收入'],
      textStyle: { color: TEXT_SECONDARY },
      top: 0,
    },
    grid: { left: 70, right: 70, top: 40, bottom: 30 },
    xAxis: {
      type: 'category',
      data: trend.dates,
      axisLine: { lineStyle: { color: BORDER_SUBTLE } },
      axisLabel: { color: TEXT_SECONDARY, fontSize: 11 },
      splitLine: { show: false },
    },
    yAxis: [
      {
        type: 'value',
        name: '支出 ($)',
        nameTextStyle: { color: TEXT_SECONDARY },
        axisLine: { show: false },
        axisLabel: {
          color: TEXT_SECONDARY,
          formatter: (v) => `$${(v / 1000).toFixed(0)}K`,
        },
        splitLine: { lineStyle: { color: BORDER_SUBTLE, type: 'dashed' } },
      },
      {
        type: 'value',
        name: '收入 ($)',
        nameTextStyle: { color: TEXT_SECONDARY },
        axisLine: { show: false },
        axisLabel: {
          color: TEXT_SECONDARY,
          formatter: (v) => `$${(v / 1000).toFixed(0)}K`,
        },
        splitLine: { show: false },
      },
    ],
    series: [
      {
        name: '支出',
        type: 'line',
        smooth: true,
        symbol: 'circle',
        symbolSize: 4,
        lineStyle: { width: 2, color: '#FC8181' },
        itemStyle: { color: '#FC8181' },
        areaStyle: {
          color: {
            type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: 'rgba(252,129,129,0.25)' },
              { offset: 1, color: 'rgba(252,129,129,0.02)' },
            ],
          },
        },
        data: trend.spend,
      },
      {
        name: '收入',
        type: 'line',
        yAxisIndex: 1,
        smooth: true,
        symbol: 'circle',
        symbolSize: 4,
        lineStyle: { width: 2, color: '#48BB78' },
        itemStyle: { color: '#48BB78' },
        areaStyle: {
          color: {
            type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: 'rgba(72,187,120,0.25)' },
              { offset: 1, color: 'rgba(72,187,120,0.02)' },
            ],
          },
        },
        data: trend.revenue,
      },
    ],
  } : null;

  // 渠道对比柱状图 — ROAS对比
  const channelBarOption = channelData.length ? {
    tooltip: {
      trigger: 'axis',
      backgroundColor: 'rgba(17,24,39,0.9)',
      borderColor: BORDER_SUBTLE,
      textStyle: { color: '#fff' },
      formatter: (params) => {
        const p = params[0];
        const ch = channelData.find(c => c.channel === p.name);
        return `<div style="font-weight:600;margin-bottom:4px">${p.name}</div>
          <div>ROAS: <strong style="color:${p.color}">${p.value}x</strong></div>
          ${ch ? `<div>收入: $${ch.revenue.toLocaleString()}</div>` : ''}
          ${ch ? `<div>支出: $${ch.spend.toLocaleString()}</div>` : ''}`;
      },
    },
    grid: { left: 50, right: 20, top: 20, bottom: 50 },
    xAxis: {
      type: 'category',
      data: channelData.map(c => c.channel),
      axisLine: { lineStyle: { color: BORDER_SUBTLE } },
      axisLabel: { color: TEXT_SECONDARY, fontSize: 12 },
      splitLine: { show: false },
    },
    yAxis: {
      type: 'value',
      name: 'ROAS (x)',
      nameTextStyle: { color: TEXT_SECONDARY },
      axisLine: { show: false },
      axisLabel: { color: TEXT_SECONDARY },
      splitLine: { lineStyle: { color: BORDER_SUBTLE, type: 'dashed' } },
    },
    series: [
      {
        name: 'ROAS',
        type: 'bar',
        barWidth: '45%',
        data: channelData.map(c => ({
          value: c.roas,
          itemStyle: {
            borderRadius: [6, 6, 0, 0],
            color: {
              type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: c.color },
                { offset: 1, color: `${c.color}66` },
              ],
            },
          },
        })),
        label: {
          show: true,
          position: 'top',
          formatter: '{c}x',
          color: TEXT_SECONDARY,
          fontSize: 13,
          fontWeight: 600,
        },
      },
    ],
  } : null;

  // 转化漏斗图
  const funnelOption = funnelData ? {
    tooltip: {
      trigger: 'item',
      backgroundColor: 'rgba(17,24,39,0.9)',
      borderColor: BORDER_SUBTLE,
      textStyle: { color: '#fff' },
      formatter: (params) => {
        const total = funnelData.values[0];
        const pct = ((params.value / total) * 100).toFixed(2);
        const stageIdx = funnelData.stages.indexOf(params.name);
        const stepRate = stageIdx > 0 ? funnelData.rates[stageIdx] : 100;
        return `<div style="font-weight:600;margin-bottom:4px">${params.name}</div>
          <div>数量: ${params.value.toLocaleString()}</div>
          <div>占曝光比: ${pct}%</div>
          ${stageIdx > 0 ? `<div>步骤转化率: ${stepRate}%</div>` : ''}`;
      },
    },
    legend: {
      data: funnelData.stages,
      textStyle: { color: TEXT_SECONDARY },
      top: 'bottom',
    },
    series: [
      {
        name: '转化漏斗',
        type: 'funnel',
        left: '15%',
        top: 20,
        bottom: 60,
        width: '70%',
        minSize: '8%',
        maxSize: '100%',
        sort: 'descending',
        gap: 4,
        label: {
          show: true,
          position: 'inside',
          formatter: (params) => `${params.name}\n${params.value.toLocaleString()}`,
          color: '#fff',
          fontSize: 12,
          fontWeight: 600,
          lineHeight: 18,
        },
        labelLine: { show: false },
        itemStyle: {
          borderRadius: 6,
          borderColor: 'var(--bg-deep, #0B0F19)',
          borderWidth: 2,
        },
        emphasis: {
          label: { fontSize: 14 },
        },
        data: funnelData.stages.map((name, i) => ({
          value: funnelData.values[i],
          name,
          itemStyle: {
            color: {
              type: 'linear', x: 0, y: 0, x2: 1, y2: 0,
              colorStops: [
                { offset: 0, color: ['#667EEA', '#00D4FF', '#F472B6', '#48BB78', '#ED8936'][i] },
                { offset: 1, color: ['rgba(102,126,234,0.5)', 'rgba(0,212,255,0.5)', 'rgba(244,114,182,0.5)', 'rgba(72,187,120,0.5)', 'rgba(237,137,54,0.5)'][i] },
              ],
            },
          },
        })),
      },
    ],
  } : null;

  // 归因饼图 — 各渠道收入贡献占比
  const attributionOption = attribution.length ? {
    tooltip: {
      trigger: 'item',
      backgroundColor: 'rgba(17,24,39,0.9)',
      borderColor: BORDER_SUBTLE,
      textStyle: { color: '#fff' },
      formatter: (params) => {
        const attrItem = attribution.find(a => a.name === params.name);
        return `<div style="font-weight:600;margin-bottom:4px">${params.name}</div>
          <div>收入: $${params.value.toLocaleString()}</div>
          <div>占比: ${params.percent}%</div>
          <div>ROAS: ${attrItem ? attrItem.roas : 'N/A'}x</div>`;
      },
    },
    legend: {
      orient: 'vertical',
      right: 10,
      top: 'center',
      textStyle: { color: TEXT_SECONDARY },
      itemWidth: 10,
      itemHeight: 10,
    },
    series: [
      {
        name: '收入归因',
        type: 'pie',
        radius: ['42%', '68%'],
        avoidLabelOverlap: true,
        center: ['38%', '50%'],
        itemStyle: {
          borderRadius: 4,
          borderColor: 'var(--bg-deep, #0B0F19)',
          borderWidth: 2,
        },
        label: {
          show: true,
          formatter: '{b}\n{d}%',
          color: TEXT_SECONDARY,
          fontSize: 11,
          lineHeight: 16,
        },
        emphasis: {
          label: { fontSize: 14, fontWeight: 'bold', color: '#fff' },
          itemStyle: { shadowBlur: 10, shadowColor: 'rgba(0,0,0,0.3)' },
        },
        data: attribution.map(a => ({
          ...a,
          itemStyle: { color: CHART_COLORS[a.name] || BRAND_PRIMARY },
        })),
      },
    ],
  } : null;

  /* ============================================================
     Table Columns — 渠道明细
     ============================================================ */
  const channelColumns = [
    {
      title: '渠道',
      dataIndex: 'channel',
      key: 'channel',
      width: 120,
      render: (text) => (
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{
            width: 8, height: 8, borderRadius: '50%',
            background: CHART_COLORS[text] || BRAND_PRIMARY,
            display: 'inline-block',
          }} />
          <span style={{ color: TEXT_PRIMARY, fontWeight: 600 }}>{text}</span>
        </span>
      ),
    },
    {
      title: '曝光量',
      dataIndex: 'impressions',
      key: 'impressions',
      align: 'right',
      width: 120,
      sorter: (a, b) => a.impressions - b.impressions,
      render: (val) => (
        <span style={{ color: TEXT_SECONDARY, fontVariantNumeric: 'tabular-nums' }}>
          {val >= 1000000 ? `${(val / 1000000).toFixed(1)}M` : val >= 1000 ? `${(val / 1000).toFixed(0)}K` : val}
        </span>
      ),
    },
    {
      title: '点击量',
      dataIndex: 'clicks',
      key: 'clicks',
      align: 'right',
      width: 110,
      sorter: (a, b) => a.clicks - b.clicks,
      render: (val) => (
        <span style={{ color: TEXT_SECONDARY, fontVariantNumeric: 'tabular-nums' }}>
          {val >= 1000 ? `${(val / 1000).toFixed(1)}K` : val}
        </span>
      ),
    },
    {
      title: 'CTR',
      dataIndex: 'ctr',
      key: 'ctr',
      align: 'right',
      width: 90,
      sorter: (a, b) => a.ctr - b.ctr,
      render: (val) => {
        const color = val >= 4 ? '#48BB78' : val >= 3 ? '#ED8936' : '#FC8181';
        return <span style={{ color, fontWeight: 600 }}>{val.toFixed(2)}%</span>;
      },
    },
    {
      title: 'CPC',
      dataIndex: 'cpc',
      key: 'cpc',
      align: 'right',
      width: 90,
      sorter: (a, b) => a.cpc - b.cpc,
      render: (val) => (
        <span style={{ color: TEXT_SECONDARY }}>${val.toFixed(2)}</span>
      ),
    },
    {
      title: 'ROAS',
      dataIndex: 'roas',
      key: 'roas',
      align: 'right',
      width: 100,
      sorter: (a, b) => a.roas - b.roas,
      render: (val) => {
        const color = val >= 3.5 ? '#48BB78' : val >= 2.5 ? '#ED8936' : '#FC8181';
        return <span style={{ color, fontWeight: 700 }}>{val.toFixed(1)}x</span>;
      },
    },
    {
      title: '收入',
      dataIndex: 'revenue',
      key: 'revenue',
      align: 'right',
      width: 120,
      sorter: (a, b) => a.revenue - b.revenue,
      render: (val) => (
        <span style={{ color: '#48BB78', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
          ${val.toLocaleString()}
        </span>
      ),
    },
    {
      title: '趋势',
      dataIndex: 'trend',
      key: 'trend',
      align: 'center',
      width: 90,
      render: (trend, record) => (
        trend === 'up' ? (
          <Tag icon={<RiseOutlined />} style={{ color: '#48BB78', background: 'rgba(72,187,120,0.1)', border: '1px solid rgba(72,187,120,0.2)', borderRadius: 6 }}>
            +{record.trendValue}%
          </Tag>
        ) : (
          <Tag icon={<FallOutlined />} style={{ color: '#FC8181', background: 'rgba(252,129,129,0.1)', border: '1px solid rgba(252,129,129,0.2)', borderRadius: 6 }}>
            {record.trendValue}%
          </Tag>
        )
      ),
    },
  ];

  const suggestions = generateSuggestions();

  /* ============================================================
     Render
     ============================================================ */
  return (
    <div style={{ padding: 24 }}>
      {/* ---- Page Header ---- */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 24,
        flexWrap: 'wrap',
        gap: 12,
      }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <h2 style={{
              margin: 0,
              fontSize: 22,
              fontWeight: 700,
              color: TEXT_PRIMARY,
              letterSpacing: '-0.02em',
            }}>
              营销效果闭环分析
            </h2>
            <Badge
              count="Agent"
              style={{
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                fontSize: 10,
                height: 20,
                lineHeight: '20px',
                padding: '0 8px',
                borderRadius: 10,
              }}
            />
          </div>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: TEXT_SECONDARY }}>
            多渠道ROAS归因 · 转化漏斗诊断 · 智能优化建议
          </p>
        </div>

        {/* Time Range Selector */}
        <Segmented
          value={period}
          onChange={setPeriod}
          options={[
            { label: '近7天', value: 7 },
            { label: '近30天', value: 30 },
            { label: '近90天', value: 90 },
          ]}
          style={{
            background: 'rgba(255,255,255,0.04)',
            color: TEXT_SECONDARY,
          }}
        />
      </div>

      {/* ---- Stat Cards Row ---- */}
      <Spin spinning={loading && !overview}>
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={12} sm={12} lg={6}>
            <StatCard
              title="ROAS总收入"
              value={`$${(overview?.totalRevenue || 0).toLocaleString()}`}
              icon={<DollarOutlined />}
              color="#48BB78"
            />
          </Col>
          <Col xs={12} sm={12} lg={6}>
            <StatCard
              title="总支出"
              value={`$${(overview?.totalSpend || 0).toLocaleString()}`}
              icon={<TeamOutlined />}
              color="#FC8181"
            />
          </Col>
          <Col xs={12} sm={12} lg={6}>
            <StatCard
              title="ROAS倍数"
              value={`${(overview?.roasRatio || 0).toFixed(2)}x`}
              icon={<ThunderboltOutlined />}
              color={BRAND_PRIMARY}
            />
          </Col>
          <Col xs={12} sm={12} lg={6}>
            <StatCard
              title="转化率"
              value={`${(overview?.conversionRate || 0).toFixed(2)}%`}
              icon={<AimOutlined />}
              color="#F472B6"
            />
          </Col>
        </Row>
      </Spin>

      {/* ---- Charts: Trend + Channel Comparison ---- */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={14}>
          <Card
            title={
              <span style={{ color: TEXT_PRIMARY, fontWeight: 600, fontSize: 15 }}>
                <DollarOutlined style={{ marginRight: 8, color: '#48BB78' }} />
                ROAS趋势（支出 vs 收入）
              </span>
            }
            style={{
              borderRadius: 'var(--radius-lg, 14px)',
              border: `1px solid ${BORDER_SUBTLE}`,
              background: 'var(--glass-bg-md, rgba(255,255,255,0.05))',
            }}
            styles={{ body: { padding: '16px 20px' } }}
          >
            {trendOption ? (
              <ReactECharts option={trendOption} style={{ height: 360 }} />
            ) : (
              <div style={{ height: 360, display: 'flex', alignItems: 'center', justifyContent: 'center', color: TEXT_TERTIARY }}>
                <Spin />
              </div>
            )}
          </Card>
        </Col>
        <Col xs={24} lg={10}>
          <Card
            title={
              <span style={{ color: TEXT_PRIMARY, fontWeight: 600, fontSize: 15 }}>
                <TrophyOutlined style={{ marginRight: 8, color: BRAND_PRIMARY }} />
                渠道ROAS对比
              </span>
            }
            style={{
              borderRadius: 'var(--radius-lg, 14px)',
              border: `1px solid ${BORDER_SUBTLE}`,
              background: 'var(--glass-bg-md, rgba(255,255,255,0.05))',
            }}
            styles={{ body: { padding: '16px 20px' } }}
          >
            {channelBarOption ? (
              <ReactECharts option={channelBarOption} style={{ height: 360 }} />
            ) : (
              <div style={{ height: 360, display: 'flex', alignItems: 'center', justifyContent: 'center', color: TEXT_TERTIARY }}>
                <Spin />
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* ---- Charts: Funnel + Attribution ---- */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={12}>
          <Card
            title={
              <span style={{ color: TEXT_PRIMARY, fontWeight: 600, fontSize: 15 }}>
                <AimOutlined style={{ marginRight: 8, color: '#667EEA' }} />
                转化漏斗
              </span>
            }
            style={{
              borderRadius: 'var(--radius-lg, 14px)',
              border: `1px solid ${BORDER_SUBTLE}`,
              background: 'var(--glass-bg-md, rgba(255,255,255,0.05))',
            }}
          >
            {funnelOption ? (
              <ReactECharts option={funnelOption} style={{ height: 380 }} />
            ) : (
              <div style={{ height: 380, display: 'flex', alignItems: 'center', justifyContent: 'center', color: TEXT_TERTIARY }}>
                <Spin />
              </div>
            )}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card
            title={
              <span style={{ color: TEXT_PRIMARY, fontWeight: 600, fontSize: 15 }}>
                <EyeOutlined style={{ marginRight: 8, color: '#F472B6' }} />
                收入归因分析
              </span>
            }
            style={{
              borderRadius: 'var(--radius-lg, 14px)',
              border: `1px solid ${BORDER_SUBTLE}`,
              background: 'var(--glass-bg-md, rgba(255,255,255,0.05))',
            }}
          >
            {attributionOption ? (
              <ReactECharts option={attributionOption} style={{ height: 380 }} />
            ) : (
              <div style={{ height: 380, display: 'flex', alignItems: 'center', justifyContent: 'center', color: TEXT_TERTIARY }}>
                <Spin />
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* ---- Channel Detail Table ---- */}
      <Card
        title={
          <span style={{ color: TEXT_PRIMARY, fontWeight: 600, fontSize: 15 }}>
            <TeamOutlined style={{ marginRight: 8, color: '#00D4FF' }} />
            渠道明细数据
          </span>
        }
        style={{
          borderRadius: 'var(--radius-lg, 14px)',
          border: `1px solid ${BORDER_SUBTLE}`,
          background: 'var(--glass-bg-md, rgba(255,255,255,0.05))',
          marginBottom: 24,
        }}
        styles={{ body: { padding: 0 } }}
      >
        <Spin spinning={loading && !channelData.length}>
          <Table
            columns={channelColumns}
            dataSource={channelData}
            rowKey="channel"
            pagination={false}
            size="middle"
            style={{ background: 'transparent' }}
          />
        </Spin>
      </Card>

      {/* ---- Optimization Suggestions (Agent) ---- */}
      <Card
        title={
          <span style={{ color: TEXT_PRIMARY, fontWeight: 600, fontSize: 15 }}>
            <ThunderboltOutlined style={{ marginRight: 8, color: BRAND_PRIMARY }} />
            AI优化建议
            <Badge
              count={suggestions.length}
              style={{ marginLeft: 8, background: 'rgba(245,200,64,0.15)', color: BRAND_PRIMARY, border: '1px solid rgba(245,200,64,0.3)' }}
            />
          </span>
        }
        style={{
          borderRadius: 'var(--radius-lg, 14px)',
          border: `1px solid ${BORDER_SUBTLE}`,
          background: 'var(--glass-bg-md, rgba(255,255,255,0.05))',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {suggestions.map((s, i) => (
            <Alert
              key={i}
              type={s.type}
              icon={s.icon}
              message={<span style={{ fontWeight: 600, color: TEXT_PRIMARY }}>{s.title}</span>}
              description={<span style={{ color: TEXT_SECONDARY }}>{s.desc}</span>}
              showIcon
              style={{
                borderRadius: 10,
                border: '1px solid rgba(255,255,255,0.06)',
                background: s.type === 'success' ? 'rgba(72,187,120,0.06)'
                  : s.type === 'warning' ? 'rgba(237,137,54,0.06)'
                  : 'rgba(102,126,234,0.06)',
              }}
            />
          ))}
        </div>
      </Card>
    </div>
  );
};

export default MarketingPage;
